import React, { useState } from "react";
import { Navigate } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { AppLayout } from "../components/app/AppLayout";
import { auth } from "../lib/api";

/**
 * Hidden SUPER_ADMIN route (`/sentry-test`) used to verify that the
 * frontend Sentry SDK is capturing errors (with Session Replay attached)
 * from the deployed production build.
 *
 * - Clicking the button throws inside a React render → caught by
 *   `SentryErrorBoundary` in `index.js`, sent to Sentry, and replaced
 *   with the friendly refresh screen. The event will appear tagged
 *   `environment: production` in the Sentry Issues feed within ~30s.
 *
 * - Available to SUPER_ADMIN only. Others are redirected home so the
 *   button never leaks to regular users.
 */
export default function SentryTest() {
  const me = auth.me();
  const [shouldThrow, setShouldThrow] = useState(false);

  if (me?.role !== "SUPER_ADMIN") return <Navigate to="/" replace />;

  if (shouldThrow) {
    // Deliberately unhandled — the SentryErrorBoundary in index.js will
    // catch this, report it, and render the "Something went wrong" screen.
    throw new Error("Sentry frontend smoke test — Break the world");
  }

  const enabled =
    !!process.env.REACT_APP_SENTRY_DSN &&
    process.env.NODE_ENV === "production";

  return (
    <AppLayout mobileTitle="Sentry test">
      <div className="mx-auto max-w-xl py-10">
        <div className="rounded-md border border-amber-200 bg-amber-50 p-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-1 h-6 w-6 shrink-0 text-amber-600" />
            <div>
              <h1 className="font-heading text-2xl font-bold text-slate-900">
                Frontend Sentry smoke test
              </h1>
              <p className="mt-2 text-sm text-slate-700">
                Clicks the "Break the world" button below, throws an
                unhandled <code>Error</code> during a React render, and
                verifies that our <code>@sentry/react</code> SDK captures
                the event and attaches a Session Replay.
              </p>
              <p className="mt-3 text-xs font-semibold uppercase tracking-wider text-slate-500">
                SDK status on this build
              </p>
              <ul className="mt-1 space-y-1 text-xs text-slate-700">
                <li>
                  DSN configured:{" "}
                  <span className={process.env.REACT_APP_SENTRY_DSN ? "font-bold text-emerald-700" : "font-bold text-red-700"}>
                    {process.env.REACT_APP_SENTRY_DSN ? "yes" : "no"}
                  </span>
                </li>
                <li>
                  Sentry env tag:{" "}
                  <span className="font-mono font-bold">
                    {process.env.REACT_APP_SENTRY_ENVIRONMENT || "(unset)"}
                  </span>
                </li>
                <li>
                  Build mode:{" "}
                  <span className="font-mono font-bold">{process.env.NODE_ENV}</span>
                </li>
                <li className="pt-1">
                  Will send to Sentry?{" "}
                  <span className={enabled ? "font-bold text-emerald-700" : "font-bold text-amber-700"}>
                    {enabled ? "YES" : "NO — preview/dev builds are gated off"}
                  </span>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-6 flex flex-col items-start gap-2">
            <button
              data-testid="sentry-break-the-world"
              onClick={() => setShouldThrow(true)}
              className="inline-flex items-center gap-2 rounded-md bg-red-600 px-5 py-3 text-sm font-bold uppercase tracking-wider text-white shadow-sm transition-colors hover:bg-red-700"
            >
              <AlertTriangle className="h-4 w-4" strokeWidth={2.5} />
              Break the world
            </button>
            {!enabled && (
              <p className="text-[11px] italic text-amber-800">
                (This build won't actually send to Sentry — click still triggers the local error boundary so you can preview the fallback UI.)
              </p>
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
